import { IfpLoansDropdown } from '@sec-loans-ui/common/steps/assistance-step/components/ipf-loans-dropdown/IfpLoansDropdown'
import { SecLoansDropdown } from '@sec-loans-ui/common/steps/assistance-step/components/sec-loans-dropdown/SecLoansDropdown'
import { WizardStep } from '@sec-loans-ui/common/wizard/rootWizardMachine'
import { useSendSecLoansPageViewEvent } from '@sec-loans-ui/hooks/useSendSecLoansPageViewEvent'
import { getSecLoansEntry } from '@sec-loans-ui/utils/helpers'
import { User } from '@sec-loans-ui/wizard/types'
import { RootStepType } from '@sec-loans-ui/wizard/wizardSteps'
import { assertValue } from '@shared/helpers'
import { SecLoansCreateApplicationResponse, SecLoansEntry } from '@shared/sec-loans'
import { useLanguage } from '@ui-common/hooks'
import { LinkButton } from '@ui-components/actions/LinkButton'
import { CollapsibleGroup } from '@ui-components/containers/CollapsibleGroup'
import { TightSectionContent } from '@ui-components/containers/TightSectionContent'
import { AssistantStep as CommonAssistantStep } from '@ui-components/layout/AssistantStep'
import { SmallHeading } from '@ui-components/typography/headings/headingPrimitives'
import { TertiaryHeading as _TertiaryHeading } from '@ui-components/typography/headings/TertiaryHeading'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { OnCreatedIfpApplicationData } from '../../../ifp-loans/types'

const TertiaryHeading = styled(_TertiaryHeading)`
  * {
    color: inherit;
  }
`

const NarrowLinkButton = styled(LinkButton)`
  width: fit-content;
`

export interface AssistanceStepProps {
  onCreatedSecLoansApplication: (data: SecLoansCreateApplicationResponse) => void
  onCreatedIfpLoansApplication: (data: OnCreatedIfpApplicationData) => void
  onGoToApplicationsClick: () => void
  onAbort: () => void
  user: User
}

export const AssistanceStep = ({
  user,
  onAbort,
  onGoToApplicationsClick,
  onCreatedSecLoansApplication,
  onCreatedIfpLoansApplication,
}: AssistanceStepProps) => {
  const { formatMessage } = useIntl()
  const { changeLanguage } = useLanguage()
  useSendSecLoansPageViewEvent(RootStepType.Assistance)

  const firstNames = assertValue(user?.firstNames, 'user.firstNames')

  const [entry] = useState(getSecLoansEntry)

  const shouldOpenSecLoansDropdown = entry === SecLoansEntry.AssistanceSecLoans
  const shouldOpenIfpLoansDropdown = entry === SecLoansEntry.AssistanceIfpLoans

  const showIfpLoansDropdown = user.ifpLoans.length > 0

  return (
    <CommonAssistantStep
      stepId={WizardStep.Assistance}
      headerButtonProps={{
        headerButtonLabel: formatMessage({ id: 'common-exit' }),
        onHeaderButtonClick: onAbort,
      }}
      changeLanguageButtonOptions={{
        label: formatMessage({ id: 'common-toggle-language' }),
        onClick: changeLanguage,
      }}
      emoji={'heyThere'}
      topic={formatMessage({ id: 'user-greeting' }, { userName: firstNames })}
      primaryHeadingLeadText={formatMessage({ id: 'loans-assistance-how-may-we-help' })}
      shouldUsePaperContainer={false}
      footer={<Footer onGoToApplicationsClick={onGoToApplicationsClick} />}
    >
      <TightSectionContent>
        <TertiaryHeading header={formatMessage({ id: 'sec-loans-catalog-tab-new-application' })} tagName={'h2'} />
        <CollapsibleGroup accordion={false}>
          <TightSectionContent>
            <SecLoansDropdown
              isOpenByDefault={shouldOpenSecLoansDropdown}
              onCreatedSecLoansApplication={onCreatedSecLoansApplication}
              hasDomesticAddress={!!user.address.domesticAddress}
            />
            {showIfpLoansDropdown && (
              <IfpLoansDropdown
                isOpenByDefault={shouldOpenIfpLoansDropdown}
                onCreatedIfpLoansApplication={onCreatedIfpLoansApplication}
                ifpLoans={user.ifpLoans}
              />
            )}
          </TightSectionContent>
        </CollapsibleGroup>
      </TightSectionContent>
    </CommonAssistantStep>
  )
}

const Footer = ({ onGoToApplicationsClick }: { onGoToApplicationsClick: () => void }) => {
  const { formatMessage } = useIntl()
  return (
    <TightSectionContent>
      <SmallHeading>{formatMessage({ id: 'loans-assistance-incomplete-applications-header' })}</SmallHeading>
      <NarrowLinkButton
        variant={'plain'}
        compact={true}
        label={formatMessage({ id: 'loans-assistance-own-applications-link-text' })}
        onClick={onGoToApplicationsClick}
      />
    </TightSectionContent>
  )
}
